import {reactive} from "vue";
import env from '../serviceWorker/environment/current.json';
import type {AvailableShopData} from "../serviceWorker/workerComponents/Models/AvailableShopData";
import type {ChosenReservationModel} from "../serviceWorker/workerComponents/Models/ChosenReservationModel";
import type {ShopModel} from "../serviceWorker/workerComponents/Models/ShopModel";
import type {ShopCategoryData} from "../serviceWorker/workerComponents/Models/ShopCategoryData";
import {get, isEmpty, mapKeys, set} from "lodash-es";
import type {RoomStandardDescriptionModel} from "../serviceWorker/workerComponents/Models/RoomStandardDescriptionModel";
import type {AdditionDescription} from "../serviceWorker/workerComponents/Models/AdditionDescription";

const apiUrl = `${env.VITE_NFHOTEL_PROTOCOL}://booking.${env.VITE_NFHOTEL_HOST}/api2/`;

async function getResponse<T = Record<string, any>>(widgetId: number, endpoint: string, query: Record<string, string | undefined>): Promise<T> {
    const parsedQuery = Object.entries(query)
        .reduce(
            (acc, [key, value]) => {
                if (value !== undefined)
                    acc[key] = value.toString();
                return acc;
            },
            {} as Record<string, string>
        );
    const response = await fetch(
        apiUrl + widgetId + '/' + endpoint + '?' + new URLSearchParams(parsedQuery),
        {method: 'GET'}
    );
    return await response.json();
}

function isAdditionAvailableInStandardFilter(standard: RoomStandardDescriptionModel) {
    return (addition:AdditionDescription) => {
        if (!addition.room_standard_tags.length) {
            return true;
        }
        for (const tag of addition.room_standard_tags) {
            if (standard.tagsById[tag]) {
                return true;
            }
        }
        return false;
    };
}

export function processShop(shop: ShopModel, reservation: ChosenReservationModel|null = null) {
    const shopData: AvailableShopData = {
        categories: [],
        articlesById: {}
    };
    if (!isEmpty(shop.additions)) {
        if (reservation?.chosenStandardDescription) {
            const standard = reservation.chosenStandardDescription;
            //region jeśli item sklepu ma tagi, to sprawdzamy czy któryś z tagów jest w standardzie, inaczej nie dopuszczamy tego itemu
            shop.additions = shop.additions.filter(isAdditionAvailableInStandardFilter(standard));
            //endregion
        }
        const categoriesById = mapKeys(shop.categories, c => c.id);

        const groupsById = mapKeys(shop.groups, c => c.id);

        for (let item of shop.additions) {
            const path = [groupsById[item.group_id].category_id, 'items'];
            const itemsArray = get(categoriesById, path, []);
            itemsArray.push(item);
            set(categoriesById, path, itemsArray);
            shopData.articlesById[item.id] = item;
        }
        shopData.categories = (shop.categories as ShopCategoryData[]).filter(c => c.items?.length);
    }
    return shopData;
}

export const NfHotelShopStore = reactive({
    shops: {} as Record<number, AvailableShopData>,
    reservations: [] as ChosenReservationModel[],
    widgetId: null as number|null,
    languageId: null as number|null,
    init(widgetId: number, languageId: number) {
        NfHotelShopStore.widgetId = widgetId;
        NfHotelShopStore.languageId = languageId;
    },
    async changeLanguage(languageId: number) {
        NfHotelShopStore.languageId = languageId;
        for (const reservation of NfHotelShopStore.reservations) {
            NfHotelShopStore.shops[reservation.reservationUniqueId] = await NfHotelShopStore.getShop(reservation);
        }
    },
    reset() {
        NfHotelShopStore.shops = {};
        NfHotelShopStore.reservations = [];
        NfHotelShopStore.widgetId = null;
    },
    async getShop(reservation: ChosenReservationModel) {
        const query = {
            lang_id: NfHotelShopStore.languageId?.toString(),
            from: reservation.reservationSearchData.dateFrom?.format('YYYY-MM-DD'),
            to: reservation.reservationSearchData.dateTo?.format('YYYY-MM-DD'),
        };
        return processShop(await getResponse<ShopModel>(this.widgetId!, 'additions', query), reservation);
    },
    getArticle(articleId: number): AdditionDescription|null {
        for (const shop of Object.values(NfHotelShopStore.shops)) {
            if (articleId in shop.articlesById)
                return shop.articlesById[articleId];
        }
        return null;
    },
    /**
     * Oblicza cenę za dany artykuł
     */
    calculateArticlePrice(article: AdditionDescription, count: number, reservation: ChosenReservationModel): number {
        switch(article.a_payment_type) {
            case 5: //Procent od ceny pobytu
                return count * reservation.calculation.prices.stay_price * article.a_price / 100;
            default:
                console.error('NfHotelShopStore.calculateArticlePrice: Unknown payment type', article.a_payment_type);
            // eslint-disable-next-line no-fallthrough
            case 1: // Zwykła cena
                return count * article.a_price;
        }
    },
    async addReservation(reservation: ChosenReservationModel) {
        if (!NfHotelShopStore.widgetId)
            throw Error('Widget id not set');
        if (reservation.reservationUniqueId in this.shops)
            console.warn('Reservation already in shop', reservation);
        else
            NfHotelShopStore.reservations.push(reservation);
        const shop = await NfHotelShopStore.getShop(reservation);
        NfHotelShopStore.shops[reservation.reservationUniqueId] = shop;
        return shop;
    },
    async removeReservation(reservation: ChosenReservationModel) {
        delete NfHotelShopStore.shops[reservation.reservationUniqueId];
        const index = NfHotelShopStore.reservations.findIndex(r => r.reservationUniqueId == reservation.reservationUniqueId);
        if (index >= 0)
            NfHotelShopStore.reservations.splice(index, 1);
    },
    canAddArticle(reservation: ChosenReservationModel, article: AdditionDescription) {
        if (article.a_max_amount === null) {
            return true;
        }
        if (isFinite(+article.a_max_amount)) {
            const maxCount = +article.a_max_amount;
            const currentCount = reservation.chosenShop.chosenItems[article.id]?.count ?? 0;
            return currentCount < maxCount;
        }
        return true;
    },
    canRemoveArticle(reservation: ChosenReservationModel, article: AdditionDescription) {
        const currentCount = reservation.chosenShop.chosenItems[article.id]?.count ?? 0;
        return currentCount > 0;
    }
});